// Le filtre et le tri d'une liste, gardés dans l'adresse.
//
// Dans l'adresse plutôt qu'en mémoire : un lien peut ouvrir une liste déjà
// filtrée (le tableau de bord mène à /factures?statut=brouillon), et revenir
// d'une fiche retrouve la liste telle qu'on l'avait laissée.

import { ecrireTri, lireTri, type SensTri, type Tri, triApresClic } from '@imp/shared/liste-triee';
import { useSearchParams } from 'react-router-dom';

export interface EtatListe<K extends string> {
  /** Le statut choisi, ou `null` pour « tous ». */
  statut: string | null;
  choisirStatut: (statut: string | null) => void;
  tri: Tri<K>;
  /** Un clic sur l'en-tête : sens naturel de la colonne, puis inversé. */
  trierPar: (cle: K, sensNaturel: SensTri) => void;
}

export function useEtatListe<K extends string>(cles: readonly K[], defaut: Tri<K>): EtatListe<K> {
  const [params, setParams] = useSearchParams();
  const tri = lireTri(params.get('tri'), cles, defaut);

  const modifier = (modif: (p: URLSearchParams) => void): void => {
    const suite = new URLSearchParams(params);
    modif(suite);
    // « replace » : trier dix fois ne doit pas demander dix retours en arrière.
    setParams(suite, { replace: true });
  };

  return {
    statut: params.get('statut'),
    choisirStatut: (statut) => {
      modifier((p) => {
        if (statut === null) p.delete('statut');
        else p.set('statut', statut);
      });
    },
    tri,
    trierPar: (cle, sensNaturel) => {
      modifier((p) => {
        const t = triApresClic(tri, cle, sensNaturel);
        // Le tri par défaut ne s'écrit pas : l'adresse reste propre.
        if (t.cle === defaut.cle && t.sens === defaut.sens) p.delete('tri');
        else p.set('tri', ecrireTri(t));
      });
    },
  };
}
